import { Record, List } from "immutable";
import { fileFromJSON, FileRecord, IFileData } from "common/records/File";
import { ISlideData } from "common/records/Slide";
import {
  IDynamicPresentationLinkData,
  DynamicPresentationLinkRecord,
  fromJSON as dplFromJSON,
  toJSON as dplToJSON,
} from "common/records/DynamicPresentationLink";
import { FileWithPreview, Nullable } from "common/types";
import { ITaskData } from "common/records/Task";
import { checkDate } from "common/helpers/Date";
import { DateStringNumber } from "common/records";

export const presentationFromJSON = (json: Partial<IPresentationData>): PresentationRecord => {
  const data: Partial<IPresentationRecord> = { ...(json as any) };

  if (json.cover_image?.url) {
    data.cover_image = fileFromJSON(json.cover_image);
  } else {
    delete data.cover_image;
  }

  if (json.task) {
    data.task_id = json.task.id;
  }

  if (json.file) {
    data.file = fileFromJSON(json.file);
  }

  if (json.slides && Array.isArray(json.slides)) {
    data.slide_ids = List(json.slides.map((s) => s.id));
  }

  if (json.dynamic_links && Array.isArray(json.dynamic_links)) {
    data.dynamic_links = List<DynamicPresentationLinkRecord>(json.dynamic_links.map((dl) => dplFromJSON(dl)));
  }

  if (json.created_at) {
    data.created_at = checkDate(json.created_at);
  }

  if (json.updated_at) {
    data.updated_at = checkDate(json.updated_at);
  }

  return new PresentationRecord(data);
};

export const toJSON = (record: PresentationRecord): IPresentationData => {
  const presentation: IPresentationData = record.toJSON() as any as IPresentationData;

  if (presentation.id <= 0) {
    delete (presentation as any).id;
  }

  presentation.dynamic_links = record.dynamic_links.map((p) => dplToJSON(p)).toArray();

  return presentation;
};

export const toFormData = (record: PresentationRecord): any => {
  const data = new FormData();
  if (record.id > 0) {
    data.set("presentation[id]", record.id as any);
  }
  data.set("presentation[name]", record.name);
  if (record.data) {
    data.set("presentation[file]", record.data);
  }
  if (record.cover_image) {
    data.set("presentation[cover_image]", record.cover_image as any);
  }
  data.set("presentation[kind]", record.kind);
  if (record.folder_id && record.folder_id > 0) {
    data.set("presentation[folder_id]", record.folder_id.toString());
  }

  return data;
};

export type PresentationKind = "default" | "link" | "dynamic";
export type PresentationTarget = "iframe" | "blank";

export interface IPresentationOptions {
  name?: string;
  kind: PresentationKind;
  folderId: Nullable<number>;
}

export interface IPresentationData {
  id: number;
  cover_image: IFileData;
  file: IFileData;
  kind: PresentationKind;
  target: PresentationTarget;
  link: string;
  name: string;
  org_id: Nullable<number>;
  job_id: Nullable<number>;
  folder_id: Nullable<number>;
  presentation_id: number;
  url: string;
  sort_order: number;
  slides?: ISlideData[];
  slide_ids?: ISlideData[];
  dynamic_links?: IDynamicPresentationLinkData[];
  loading?: boolean;
  selected?: boolean;
  data?: Nullable<File>;
  task?: ITaskData;

  created_at: DateStringNumber;
  updated_at: DateStringNumber;
}

export interface IPresentationRecord {
  id: number;
  cover_image: Nullable<FileRecord | FileWithPreview>;
  file: Nullable<FileRecord>;
  kind: PresentationKind;
  target: PresentationTarget;
  link: string;
  name: string;
  org_id: Nullable<number>;
  job_id: Nullable<number>;
  folder_id: Nullable<number>;
  presentation_id: Nullable<number>;
  url: Nullable<string>;
  sort_order: Nullable<number>;
  slide_ids: List<number>;
  dynamic_links: List<DynamicPresentationLinkRecord>;
  loading: boolean;
  selected: boolean;
  errors: List<string>;
  data?: Nullable<File>;
  task_id?: Nullable<string>;

  created_at: Date;
  updated_at: Date;
}

const defaultPresentationProps: IPresentationRecord = {
  id: 0,
  cover_image: null,
  file: null,
  kind: "default",
  target: "iframe",
  link: "",
  name: "",
  org_id: null,
  job_id: null,
  folder_id: null,
  presentation_id: null,
  url: null,
  sort_order: null,
  slide_ids: List<number>(),
  dynamic_links: List<DynamicPresentationLinkRecord>(),
  loading: false,
  selected: false,
  errors: List<string>(),
  data: null,
  task_id: null,
  created_at: new Date(),
  updated_at: new Date(),
};

export class PresentationRecord extends Record(defaultPresentationProps) implements IPresentationRecord {}
